import { GOOGLE_DRIVE_FILE_SCOPE, GOOGLE_SLIDES_MIME_TYPE, type GoogleConfig } from './googleConfig';
import { GoogleScriptError, loadGoogleIdentityServices, loadGooglePicker } from './googleScripts';

// Google Identity Services + Google Picker voor de Google Slides-import.
//
// Tokenbeleid: het access-token is kortlevend (~1 uur), wordt alleen als
// functiewaarde doorgegeven en nooit in state, storage, een URL of een log
// bewaard. Er is geen refresh-token. Het token gaat uitsluitend naar de
// Picker (die het vereist) en eenmalig in de body van de import-/hersync-
// aanroep naar de eigen backend. We roepen bewust GEEN revoke aan: dat zou ook
// de toestemming van de gebruiker intrekken en de per-bestand-toegang wissen.

export type GoogleAuthErrorCode = 'cancelled' | 'denied' | 'scope' | 'unavailable' | 'popup_blocked';

export class GoogleAuthError extends Error {
  code: GoogleAuthErrorCode;

  constructor(code: GoogleAuthErrorCode, message: string) {
    super(message);
    this.name = 'GoogleAuthError';
    this.code = code;
  }
}

export interface PickedPresentation {
  id: string;
  name: string;
}

/** Vraagt een kortlevend access-token voor `drive.file`. Moet vanuit een
 * klik-handler worden aangeroepen (browsers blokkeren anders de popup). */
export async function requestDriveFileToken(clientId: string): Promise<string> {
  try {
    await loadGoogleIdentityServices();
  } catch (error) {
    if (error instanceof GoogleScriptError) throw new GoogleAuthError('unavailable', error.message);
    throw error;
  }
  const oauth2 = (window as any).google.accounts.oauth2;

  return new Promise<string>((resolve, reject) => {
    const client = oauth2.initTokenClient({
      client_id: clientId,
      scope: GOOGLE_DRIVE_FILE_SCOPE,
      callback: (response: any) => {
        if (response?.error) {
          reject(new GoogleAuthError(
            response.error === 'access_denied' ? 'denied' : 'unavailable',
            response.error === 'access_denied'
              ? 'Je hebt Zoea geen toegang gegeven tot Google. Zonder toestemming kan Zoea de presentatie niet ophalen.'
              : 'Verbinden met Google is niet gelukt. Probeer het opnieuw.',
          ));
          return;
        }
        // De gebruiker kan toestemming gedeeltelijk geven: controleer de scope altijd.
        if (!oauth2.hasGrantedAllScopes(response, GOOGLE_DRIVE_FILE_SCOPE) || !response.access_token) {
          reject(new GoogleAuthError('scope', 'Zoea heeft toestemming nodig om de gekozen presentatie te lezen. Probeer het opnieuw en geef toestemming.'));
          return;
        }
        resolve(response.access_token as string);
      },
      error_callback: (error: any) => {
        if (error?.type === 'popup_closed') {
          reject(new GoogleAuthError('cancelled', 'Het Google-venster is gesloten voordat je toestemming gaf.'));
        } else if (error?.type === 'popup_failed_to_open') {
          reject(new GoogleAuthError('popup_blocked', 'Je browser blokkeert het Google-venster. Sta pop-ups voor Zoea toe en probeer opnieuw.'));
        } else {
          reject(new GoogleAuthError('unavailable', 'Verbinden met Google is niet gelukt. Probeer het opnieuw.'));
        }
      },
    });
    client.requestAccessToken({});
  });
}

/** Toont de Google Picker met alleen Google Slides-presentaties. Geeft null bij
 * annuleren. Met `drive.file` krijgt Zoea alleen toegang tot het gekozen bestand. */
export async function pickPresentation(accessToken: string, config: GoogleConfig): Promise<PickedPresentation | null> {
  try {
    await loadGooglePicker();
  } catch (error) {
    if (error instanceof GoogleScriptError) throw new GoogleAuthError('unavailable', error.message);
    throw error;
  }
  const picker = (window as any).google.picker;

  return new Promise<PickedPresentation | null>((resolve) => {
    const view = new picker.DocsView(picker.ViewId.PRESENTATIONS)
      .setIncludeFolders(false)
      .setMimeTypes(GOOGLE_SLIDES_MIME_TYPE);
    const instance = new picker.PickerBuilder()
      .setAppId(config.projectNumber)
      .setDeveloperKey(config.apiKey)
      .setOAuthToken(accessToken)
      .setLocale('nl')
      .addView(view)
      .setCallback((data: any) => {
        const action = data?.[picker.Response.ACTION];
        if (action === picker.Action.PICKED) {
          const doc = data[picker.Response.DOCUMENTS]?.[0];
          const id = doc?.[picker.Document.ID];
          resolve(id ? { id, name: doc[picker.Document.NAME] || 'Presentatie' } : null);
        } else if (action === picker.Action.CANCEL) {
          resolve(null);
        }
      })
      .build();
    instance.setVisible(true);
  });
}

/** Nederlandse melding voor een fout uit dit bestand of uit de backend. */
export function describeGoogleError(error: any, fallback: string): string {
  if (error instanceof GoogleAuthError) return error.message;
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}
